import './CustomRedactionTextAccordion.scss';
import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ReactComponent as DeleteIcon} from '../../../../../assets/images/delete.svg';
import ArrButton from '../../../../common/arr-button/ArrButton';
import TagsSection from '../tags-section/TagsSection';
import TextPropertyToolbar from '../text-property-toolbar/TextPropertyToolbar';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {TagItem} from '../../../../../api/types/production.type';
import {ISelectItem} from '../../../../../shared/types';
import {t} from 'i18next';

export interface RedactionText {
  selectedTags: TagItem[];
  disabledTags?: TagItem[];
  placeholderText: string;
  abbreviatedText: string;
}

interface CustomRedactionTextAccordionProps {
  id: string;
  index: number;
  redactionTextData: RedactionText;
  onDelete: (id: string) => void;
  onRedactionTextDataChange: (id: string, data: RedactionText) => void;
  tagsTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  insertFieldTypes: ISelectItem[];
  metadataFieldOptions: ISelectItem[];
  productionFieldOptions: ISelectItem[];
  disabled?: boolean;
}

const CustomRedactionTextAccordion: React.FC<
  CustomRedactionTextAccordionProps
> = ({
  id,
  index,
  redactionTextData,
  onDelete,
  onRedactionTextDataChange,
  tagsTreeData = [],
  insertFieldTypes,
  metadataFieldOptions,
  productionFieldOptions,
  disabled = false,
}) => {
  const handleTagsChange = (tags: TagItem[]) => {
    if (disabled) return;
    onRedactionTextDataChange(id, {
      ...redactionTextData,
      selectedTags: tags,
    });
  };

  const handleTextChange = (text: string) => {
    if (disabled) return;
    onRedactionTextDataChange(id, {
      ...redactionTextData,
      placeholderText: text,
    });
  };

  const handleAbbreviatedTextChange = (text: string) => {
    if (disabled) return;
    onRedactionTextDataChange(id, {
      ...redactionTextData,
      abbreviatedText: text,
    });
  };

  return (
    <Box className="custom-redaction-text-accordion-wrapper">
      <Box className="custom-redaction-text-header-wrapper">
        <Accordion className="custom-redaction-text-accordion" defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className="custom-redaction-text-accordion-summary"
          >
            <Typography
              className="typo-text subhead4_size custom-redaction-text-title"
              data-testid={`custom-redaction-text-accordion-title`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.title',
                {number: index + 1}
              )}
            </Typography>
          </AccordionSummary>

          <AccordionDetails className="custom-redaction-text-details">
            <Box className="custom-redaction-text-content">
              {/* Tags Section */}
              <TagsSection
                headerTitle={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionTags.label'
                )}
                buttonTitle={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionTags.buttonText'
                )}
                selectedTags={redactionTextData.selectedTags}
                onTagsChange={handleTagsChange}
                tagsTreeData={tagsTreeData}
                disabledTags={redactionTextData.disabledTags}
                disabled={disabled}
              />

              {/* Text Property Toolbar */}
              <TextPropertyToolbar
                variant="with-title"
                dataTestId="with-title"
                title={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.placeholderText.title'
                )}
                toolbarText={redactionTextData.placeholderText}
                insertFieldTypes={insertFieldTypes}
                metadataFieldOptions={metadataFieldOptions}
                productionFieldOptions={productionFieldOptions}
                onToolbarTextChange={handleTextChange}
                onInsertField={(field) => {
                  handleTextChange(
                    `${redactionTextData.placeholderText}$${field}$`
                  );
                }}
                placeholder={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.placeholderText.placeholder'
                )}
                disabled={disabled}
              />

              {/* Text Property Toolbar */}
              <TextPropertyToolbar
                dataTestId="with-only-title"
                variant="with-only-title"
                title={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.abbreviatedText.title'
                )}
                toolbarText={redactionTextData.abbreviatedText}
                onToolbarTextChange={handleAbbreviatedTextChange}
                placeholder={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.abbreviatedText.placeholder'
                )}
                disabled={disabled}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
        {!disabled && (
          <ArrButton
            variant="secondary"
            size="small"
            onClick={() => onDelete(id)}
            className="delete-btn"
            dataTestId="custom-redaction-text-accordion-button"
          >
            <DeleteIcon />
          </ArrButton>
        )}
      </Box>
    </Box>
  );
};

export default CustomRedactionTextAccordion;
